/** Scoring syntax v1: binary64 arithmetic, bounded after every operation. */
export const EXPRESSION_VERSION = 1;
export const SCORING_METRICS = [
  'wires',
  'notGates',
  'diodes',
  'totalTiles',
  'area',
] as const;
export type ScoringMetric = (typeof SCORING_METRICS)[number];
export type ScoreMetrics = Record<ScoringMetric, number>;
export const expressionLimits = Object.freeze({
  characters: 256,
  tokens: 128,
  nodes: 64,
  depth: 16,
  exponent: 8,
  constant: 1_000_000,
  value: 1_000_000_000_000,
});
type BinaryOperator = '+' | '-' | '*' | '/';
export type ScoreExpression =
  | { kind: 'number'; value: number }
  | { kind: 'metric'; name: ScoringMetric }
  | { kind: 'unary'; operator: '+' | '-'; argument: ScoreExpression }
  | {
      kind: 'binary';
      operator: BinaryOperator;
      left: ScoreExpression;
      right: ScoreExpression;
    }
  | { kind: 'power'; base: ScoreExpression; exponent: number };
type Token = { text: string; at: number; number?: number };
export class ScoringExpressionError extends Error {}

function bounded(value: number) {
  if (!Number.isFinite(value))
    throw new ScoringExpressionError('The formula produced a nonfinite score.');
  if (Math.abs(value) > expressionLimits.value)
    throw new ScoringExpressionError(
      'An intermediate score exceeds the supported range of ±1,000,000,000,000.',
    );
  return Object.is(value, -0) ? 0 : value;
}
function arithmetic(operator: BinaryOperator, left: number, right: number) {
  if (operator === '/' && right === 0)
    throw new ScoringExpressionError('The formula divides by zero.');
  return bounded(
    operator === '+'
      ? left + right
      : operator === '-'
        ? left - right
        : operator === '*'
          ? left * right
          : left / right,
  );
}
function tokenize(source: string): Token[] {
  if (!source.trim()) throw new ScoringExpressionError('Enter a scoring formula.');
  if (source.length > expressionLimits.characters)
    throw new ScoringExpressionError('Keep the formula to 256 characters or fewer.');
  const tokens: Token[] = [];
  let at = 0;
  while (at < source.length) {
    if (source[at].trim() === '') {
      at++;
      continue;
    }
    const start = at;
    const number = source.slice(at).match(/^(?:[0-9]+(?:[.][0-9]*)?|[.][0-9]+)/)?.[0];
    if (number) {
      const value = Number(number);
      if (!Number.isFinite(value) || value > expressionLimits.constant)
        throw new ScoringExpressionError('Formula constants must be at most 1,000,000.');
      tokens.push({ text: number, number: value, at });
      at += number.length;
    } else {
      const name = source.slice(at).match(/^[A-Za-z][A-Za-z0-9]*/)?.[0];
      if (name) {
        if (!(SCORING_METRICS as readonly string[]).includes(name))
          throw new ScoringExpressionError(`Unknown scoring metric: ${name}.`);
        tokens.push({ text: name, at });
        at += name.length;
      } else if ('+-*/^()'.includes(source[at])) {
        if (source.slice(at, at + 2) === '**')
          throw new ScoringExpressionError(
            'Use ^ for powers, with a whole-number exponent from 0 to 8.',
          );
        tokens.push({ text: source[at++], at: start });
      } else
        throw new ScoringExpressionError(
          `Unsupported character at position ${at + 1}. Use metrics, numbers, + - * / ^ and parentheses.`,
        );
    }
    if (tokens.length > expressionLimits.tokens)
      throw new ScoringExpressionError('The formula has too many tokens.');
  }
  return tokens;
}
class Parser {
  private position = 0;
  private nodes = 0;
  private nesting = 0;
  constructor(private tokens: Token[]) {}
  private peek(text: string) {
    return this.tokens[this.position]?.text === text;
  }
  private take() {
    return this.tokens[this.position++];
  }
  private node<T extends ScoreExpression>(value: T): T {
    if (++this.nodes > expressionLimits.nodes)
      throw new ScoringExpressionError('The formula exceeds 64 expression nodes.');
    return value;
  }
  private nested(read: () => ScoreExpression) {
    if (++this.nesting > expressionLimits.depth)
      throw new ScoringExpressionError('The formula is nested too deeply (maximum 16).');
    const value = read();
    this.nesting--;
    return value;
  }
  parse(): ScoreExpression {
    const value = this.sum();
    if (this.position !== this.tokens.length)
      throw new ScoringExpressionError(
        `Unexpected token at position ${this.tokens[this.position].at + 1}.`,
      );
    const pending = [{ value, depth: 1 }];
    while (pending.length) {
      const entry = pending.pop()!;
      if (entry.depth > expressionLimits.depth)
        throw new ScoringExpressionError(
          'The formula is nested too deeply (maximum 16).',
        );
      const children =
        entry.value.kind === 'binary'
          ? [entry.value.left, entry.value.right]
          : entry.value.kind === 'unary'
            ? [entry.value.argument]
            : entry.value.kind === 'power'
              ? [entry.value.base]
              : [];
      pending.push(
        ...children.map((child) => ({ value: child, depth: entry.depth + 1 })),
      );
    }
    constantValue(value);
    return value;
  }
  private sum(): ScoreExpression {
    let value = this.product();
    while (this.peek('+') || this.peek('-')) {
      const operator = this.take().text as '+' | '-';
      value = this.node({ kind: 'binary', operator, left: value, right: this.product() });
    }
    return value;
  }
  private product(): ScoreExpression {
    let value = this.unary();
    while (this.peek('*') || this.peek('/')) {
      const operator = this.take().text as '*' | '/';
      value = this.node({ kind: 'binary', operator, left: value, right: this.unary() });
    }
    return value;
  }
  private unary(): ScoreExpression {
    if (this.peek('+') || this.peek('-')) {
      const operator = this.take().text as '+' | '-';
      return this.node({
        kind: 'unary',
        operator,
        argument: this.nested(() => this.unary()),
      });
    }
    let value = this.primary();
    if (this.peek('^')) {
      this.take();
      const exponent = this.take();
      if (
        !exponent ||
        exponent.number === undefined ||
        !Number.isInteger(exponent.number) ||
        exponent.number > expressionLimits.exponent
      )
        throw new ScoringExpressionError(
          'A power needs a whole-number literal exponent from 0 to 8.',
        );
      value = this.node({ kind: 'power', base: value, exponent: exponent.number });
      if (this.peek('^'))
        throw new ScoringExpressionError(
          'Chained powers are not supported. Use a single exponent from 0 to 8.',
        );
    }
    return value;
  }
  private primary(): ScoreExpression {
    const token = this.take();
    if (!token) throw new ScoringExpressionError('The formula is missing a value.');
    if (token.number !== undefined)
      return this.node({ kind: 'number', value: token.number });
    if ((SCORING_METRICS as readonly string[]).includes(token.text))
      return this.node({ kind: 'metric', name: token.text as ScoringMetric });
    if (token.text === '(') {
      const value = this.nested(() => this.sum());
      if (!this.peek(')'))
        throw new ScoringExpressionError('Close every opening parenthesis.');
      this.take();
      return value;
    }
    throw new ScoringExpressionError(
      `Expected a metric, number or parenthesis at position ${token.at + 1}.`,
    );
  }
}

/** Catch unconditional zero divisors and constant overflows before publishing. */
function constantValue(node: ScoreExpression): number | undefined {
  if (node.kind === 'number') return node.value;
  if (node.kind === 'metric') return undefined;
  if (node.kind === 'unary') {
    const value = constantValue(node.argument);
    return value === undefined
      ? undefined
      : bounded(node.operator === '-' ? -value : value);
  }
  if (node.kind === 'power') {
    const base = constantValue(node.base);
    return base === undefined ? undefined : bounded(base ** node.exponent);
  }
  const left = constantValue(node.left),
    right = constantValue(node.right);
  if (node.operator === '/' && right === 0)
    throw new ScoringExpressionError('The formula divides by zero.');
  return left === undefined || right === undefined
    ? undefined
    : arithmetic(node.operator, left, right);
}
export function parseScoreExpression(formula: string): ScoreExpression {
  return new Parser(tokenize(formula)).parse();
}
function display(node: ScoreExpression): string {
  if (node.kind === 'number') return String(node.value);
  if (node.kind === 'metric') return node.name;
  if (node.kind === 'unary') return `(${node.operator}${display(node.argument)})`;
  if (node.kind === 'power') return `(${display(node.base)} ^ ${node.exponent})`;
  return `(${display(node.left)} ${node.operator} ${display(node.right)})`;
}
export interface ScoreEvaluation {
  value: number;
  metrics: Partial<ScoreMetrics>;
  breakdown: { expression: string; value: number }[];
}
export function evaluateScoreExpression(
  formula: string,
  metrics: ScoreMetrics,
): ScoreEvaluation {
  const root = parseScoreExpression(formula);
  for (const name of SCORING_METRICS) {
    const value = metrics[name];
    if (!Number.isSafeInteger(value) || value < 0 || value > expressionLimits.value)
      throw new ScoringExpressionError(
        `Metric ${name} must be a nonnegative bounded integer.`,
      );
  }
  const used: Partial<ScoreMetrics> = {};
  const breakdown: ScoreEvaluation['breakdown'] = [];
  const evaluate = (node: ScoreExpression): number => {
    if (node.kind === 'number') return node.value;
    if (node.kind === 'metric') {
      used[node.name] = metrics[node.name];
      return metrics[node.name];
    }
    const value =
      node.kind === 'unary'
        ? bounded((node.operator === '-' ? -1 : 1) * evaluate(node.argument))
        : node.kind === 'power'
          ? bounded(evaluate(node.base) ** node.exponent)
          : arithmetic(node.operator, evaluate(node.left), evaluate(node.right));
    breakdown.push({ expression: display(node), value });
    return value;
  };
  const value = evaluate(root);
  return { value, metrics: used, breakdown };
}
